import type {Component, Editor} from 'grapesjs';
import {isMediaComponent, resolveSectorsForComponent} from '@EmailMakerReact/layout/email-maker-right-panel/right-panel-registry';
import {isIconButtonControl} from '@EmailMakerReact/layout/email-maker-right-panel/email-maker-right-panel.util';
import type {EmailMakerSectorProperty, EmailMakerSectorVisibilityCtx} from '@EmailMakerReact/layout/email-maker-right-panel/right-panel-registry.types';
import {
  editDesignComponent,
  getAiImagesActionForComponent,
  PROMOTED_MEDIA_ACTION_META,
  PromotedMediaAction,
  removeBackgroundForComponent,
} from '@EmailMakerReact/layout/email-maker-right-panel/controls/mod-list-control/mod-list-control-descriptors';
import type {EmailMakerImmutableStore} from '@EmailMakerReact/email-maker-react.types';
import {EMAIL_MAKER_BLOCKS, EMAIL_MAKER_COMPONENT_TYPES} from '@EmailMakerReact/email-maker-react.constants';
import {openImageCropper} from '@EmailMakerReact/lib/email-maker-media-library';
import {openAddImageOptionSelector, openAddVideoOptionSelector, openAiImages} from '@EmailMakerReact/lib/email-maker-media-modals-library';
import {isVideoMediaComponent} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {TileComplexity} from './email-maker-tile-meta-library.types';
import type {
  BlockCategoryTileEntry,
  EmailMakerActionMeta,
  EmailMakerControlTileEntry,
  EmailMakerResolvedAction,
  EmailMakerResolvedControlAtIndex,
} from './email-maker-tile-meta-library.types';

export {TileComplexity};

export const BLOCK_CATEGORY_TILES: readonly BlockCategoryTileEntry[] = [
  {id: EMAIL_MAKER_BLOCKS.CATEGORY_SECTIONS, icon: 'icon-section', labelKey: 'pmwjs_sections'},
  {id: EMAIL_MAKER_BLOCKS.CATEGORY_ELEMENTS, icon: 'icon-elements', labelKey: 'pmwjs_elements'},
  {id: EMAIL_MAKER_BLOCKS.CATEGORY_TEXT, icon: 'icon-text', labelKey: 'pmwjs_text'},
  {id: EMAIL_MAKER_BLOCKS.CATEGORY_IMAGE, icon: 'icon-image', labelKey: 'pmwjs_image'},
  {id: EMAIL_MAKER_BLOCKS.CATEGORY_MY_DESIGNS, icon: 'icon-designs', labelKey: 'pmwjs_my_designs'},
  {id: EMAIL_MAKER_BLOCKS.BLOCK_AI_EDIT, icon: 'icon-sparkle', labelKey: 'pmwjs_ai_edit'},
] as const;

/** Iterates every visible property across sectors in mobile order, passing each property and its stable flatIndex to `cb`. */
function forEachVisibleProperty(editor: Editor, selected: Component, store: EmailMakerImmutableStore, cb: (prop: EmailMakerSectorProperty, flatIndex: number) => void): void {
  const ctx: EmailMakerSectorVisibilityCtx = {component: selected, editor, store};
  const sectors = resolveSectorsForComponent(editor, selected, store);
  let flatIndex = 0;

  for (const sector of sectors) {
    const properties = sector.mobileProperties ?? sector.properties;
    const visibleProperties = properties.filter((prop) => !prop.isVisible || prop.isVisible(ctx));

    for (const prop of visibleProperties) {
      cb(prop, flatIndex);
      flatIndex += 1;
    }
  }
}

/**
 * Resolves the ordered list of per-selection control tile entries for the current component.
 * Properties without `tileMeta` (style headers, info boxes) are skipped but still increment flatIndex to keep indices stable.
 */
export function getResolvedTiles(editor: Editor, selected: Component, store: EmailMakerImmutableStore): EmailMakerControlTileEntry[] {
  const tiles: EmailMakerControlTileEntry[] = [];

  forEachVisibleProperty(editor, selected, store, (prop, flatIndex) => {
    const {tileMeta} = prop;
    if (!tileMeta) {
      return;
    }
    const target = resolveControlTarget(selected, prop.resolveTarget, editor);
    tiles.push({control: prop.control, meta: tileMeta, target, flatIndex});
  });

  return tiles;
}

/** Resolves the ordered list of action controls for the mobile action strip. Promoted media actions are appended after sector-derived actions. */
export function getResolvedActions(editor: Editor, selected: Component, store: EmailMakerImmutableStore): EmailMakerResolvedAction[] {
  const editActions: EmailMakerResolvedAction[] = [];
  const managementActions: EmailMakerResolvedAction[] = [];

  forEachVisibleProperty(editor, selected, store, (prop, flatIndex) => {
    if (!isActionProperty(prop)) {
      return;
    }
    const target = resolveControlTarget(selected, prop.resolveTarget, editor);
    const action: EmailMakerResolvedAction = {control: prop.control, target, key: String(flatIndex), flatIndex, actionMeta: prop.actionMeta};
    (prop.actionMeta ? editActions : managementActions).push(action);
  });

  return [...editActions, ...getPromotedMediaActions(selected), ...managementActions];
}

/** Sentinel flatIndex for promoted actions: they always run `onActivate`, so they never resolve a sheet by index. */
const PROMOTED_ACTION_FLAT_INDEX = -1;

/** Builds one promoted action entry from a shared icon/label, a complexity, and the handler to run on tap. */
function makePromotedAction(action: PromotedMediaAction, complexity: TileComplexity, target: Component, onActivate: EmailMakerActionMeta['onActivate']): EmailMakerResolvedAction {
  const {icon, labelKey, mobileLabelKey} = PROMOTED_MEDIA_ACTION_META[action];
  return {
    target,
    key: `promoted-${action}`,
    flatIndex: PROMOTED_ACTION_FLAT_INDEX,
    actionMeta: {icon, labelKey: mobileLabelKey ?? labelKey, complexity, onActivate},
  };
}

/** Promoted media actions for the current selection, mirroring per-type applicability of the mod-list controls. Returns `[]` for non-media components. */
function getPromotedMediaActions(selected: Component): EmailMakerResolvedAction[] {
  if (selected.is(EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_DESIGN)) {
    return [
      makePromotedAction(PromotedMediaAction.EDIT_DESIGN, TileComplexity.COMPLEX, selected, ({editor: ed, target}): void => {
        editDesignComponent(ed, target);
      }),
      makePromotedAction(PromotedMediaAction.REPLACE, TileComplexity.SIMPLE, selected, ({editor: ed, target}): void => {
        openAddImageOptionSelector(ed, target);
      }),
    ];
  }

  if (!isMediaComponent(selected)) {
    return [];
  }

  if (isVideoMediaComponent(selected)) {
    return [
      makePromotedAction(PromotedMediaAction.REPLACE, TileComplexity.SIMPLE, selected, ({editor: ed, target}): void => {
        openAddVideoOptionSelector(ed, target);
      }),
    ];
  }

  return [
    makePromotedAction(PromotedMediaAction.CROP, TileComplexity.SIMPLE, selected, ({editor: ed, target}): void => {
      openImageCropper(ed, target);
    }),
    makePromotedAction(PromotedMediaAction.REPLACE, TileComplexity.SIMPLE, selected, ({editor: ed, target}): void => {
      openAddImageOptionSelector(ed, target);
    }),
    makePromotedAction(PromotedMediaAction.REMOVE_BACKGROUND, TileComplexity.SIMPLE, selected, ({editor: ed, target}): void => {
      void removeBackgroundForComponent(ed, target);
    }),
    makePromotedAction(getAiImagesActionForComponent(selected), TileComplexity.SIMPLE, selected, ({editor: ed, target}): void => {
      openAiImages(ed, target);
    }),
  ];
}

/** An action is an instant/toggle icon-button control, or a sheet-opening control opted in via `actionMeta`. */
function isActionProperty(prop: EmailMakerSectorProperty): boolean {
  return isIconButtonControl(prop.control) || Boolean(prop.actionMeta);
}

/**
 * Returns the control + resolved target at `flatIndex` in the visible sector-ordered property list.
 * Returns `undefined` when the property at that index has neither tile nor action meta (e.g. style headers).
 */
export function resolveControlByFlatIndex(editor: Editor, selected: Component, store: EmailMakerImmutableStore, targetIndex: number): EmailMakerResolvedControlAtIndex | undefined {
  let result: EmailMakerResolvedControlAtIndex | undefined;

  forEachVisibleProperty(editor, selected, store, (prop, flatIndex) => {
    if (result !== undefined || flatIndex !== targetIndex) {
      return;
    }
    const labelKey = prop.tileMeta?.labelKey ?? prop.actionMeta?.labelKey;
    if (labelKey === undefined) {
      result = undefined;
      return;
    }
    const target = resolveControlTarget(selected, prop.resolveTarget, editor);
    result = {control: prop.control, target, labelKey};
  });

  return result;
}

/** Resolves the effective target component, substituting the PMW_CONTENT child for wrapper-sector controls. */
export function resolveControlTarget(selected: Component, resolveTarget: ((selected: Component, editor: Editor) => Component | undefined) | undefined, editor: Editor): Component {
  if (!resolveTarget) {
    return selected;
  }
  return resolveTarget(selected, editor) ?? selected;
}
